import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, User, Mail, Phone, MapPin, Building2, Target,
  CheckCircle2, ArrowLeft, ArrowRight, Loader2, Send, Sparkles,
} from 'lucide-react';
import { sendPublicFormEmail } from '../../lib/public-form-email';

interface EMPApplicationFormProps {
  isOpen: boolean;
  onClose: () => void;
}

type Status = 'idle' | 'success' | 'error';

const initialForm = {
  fullName: '', email: '', phone: '',
  country: '', state: '', city: '', address: '',
  companyName: '', businessType: '', yearsInExport: '',
  products: '', targetMarket: '', challenges: '',
  availability: '', investmentReadiness: '', whySelect: '',
};

const steps = [
  { id: 1, label: 'Personal', icon: User },
  { id: 2, label: 'Location', icon: MapPin },
  { id: 3, label: 'Business', icon: Building2 },
  { id: 4, label: 'Export', icon: Target },
  { id: 5, label: 'Fit', icon: Sparkles },
];

const EMPApplicationForm: React.FC<EMPApplicationFormProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState<Status>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [website, setWebsite] = useState('');

  if (!isOpen) return null;

  const update = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const canProceed = (() => {
    switch (step) {
      case 1: return form.fullName && form.email && form.phone;
      case 2: return form.country && form.state && form.city;
      case 3: return form.companyName && form.businessType && form.yearsInExport;
      case 4: return form.products && form.targetMarket && form.challenges;
      default: return true;
    }
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (website) {
        setStatus('success');
        setForm(initialForm);
        setStep(1);
        return;
      }
      await sendPublicFormEmail({ formType: 'emp-application', ...form, website });
      setStatus('success');
      setForm(initialForm);
      setStep(1);
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setStatus('idle');
    setStep(1);
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={close}
        />
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 px-6 sm:px-8 py-6 text-white relative flex-shrink-0">
              <button onClick={close} className="absolute top-4 right-4 text-white/80 hover:text-white">
                <X className="h-5 w-5" />
              </button>
              <span className="inline-block px-3 py-1 bg-white/15 text-xs font-medium rounded-full mb-2">
                Scale-Up Program
              </span>
              <h3 className="text-2xl font-bold">Export Mentorship Program</h3>
              <p className="text-blue-100 text-sm mt-1">
                Apply for the next cohort · Limited to 20 participants
              </p>

              {/* Stepper */}
              <div className="mt-6 flex items-center justify-between">
                {steps.map((s, i) => {
                  const active = step === s.id;
                  const done = step > s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                          done ? 'bg-emerald-500 text-white' :
                          active ? 'bg-white text-blue-700' :
                          'bg-white/20 text-white/70'
                        }`}>
                          {done ? <CheckCircle2 className="h-4 w-4" /> : s.id}
                        </div>
                        <span className={`mt-1 text-[10px] sm:text-xs ${active ? 'text-white' : 'text-white/60'}`}>
                          {s.label}
                        </span>
                      </div>
                      {i < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 sm:mx-2 mb-4 ${step > s.id ? 'bg-emerald-500' : 'bg-white/20'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-6 sm:px-8 py-6">
              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Application Received!</h4>
                  <p className="text-gray-600 mb-6">
                    Thanks {form.fullName || 'for applying'}. Our admissions team will review your
                    application and reach out within 3 business days.
                  </p>
                  <button onClick={close} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="hidden" aria-hidden="true">
                    <label>
                      Website
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                      />
                    </label>
                  </div>
                  {status === 'error' && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  {step === 1 && (
                    <StepShell title="Personal Information" subtitle="Tell us who you are.">
                      <Input icon={User} label="Full Name" value={form.fullName} onChange={(v) => update('fullName', v)} required />
                      <Input icon={Mail} label="Email Address" type="email" value={form.email} onChange={(v) => update('email', v)} required />
                      <Input icon={Phone} label="Phone Number" type="tel" value={form.phone} onChange={(v) => update('phone', v)} required />
                    </StepShell>
                  )}

                  {step === 2 && (
                    <StepShell title="Location" subtitle="Where are you based?">
                      <div className="grid sm:grid-cols-3 gap-4">
                        <Input label="Country" value={form.country} onChange={(v) => update('country', v)} required />
                        <Input label="State" value={form.state} onChange={(v) => update('state', v)} required />
                        <Input label="City" value={form.city} onChange={(v) => update('city', v)} required />
                      </div>
                      <Input icon={MapPin} label="Address (optional)" value={form.address} onChange={(v) => update('address', v)} />
                    </StepShell>
                  )}

                  {step === 3 && (
                    <StepShell title="Business Information" subtitle="Tell us about your company.">
                      <Input icon={Building2} label="Company / Business Name" value={form.companyName} onChange={(v) => update('companyName', v)} required />
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Select label="Business Type" value={form.businessType} onChange={(v) => update('businessType', v)} required
                          options={[
                            ['sole-proprietorship', 'Sole Proprietorship'],
                            ['partnership', 'Partnership'],
                            ['limited-liability', 'Limited Liability Company'],
                            ['corporation', 'Corporation'],
                          ]} />
                        <Select label="Years in Export" value={form.yearsInExport} onChange={(v) => update('yearsInExport', v)} required
                          options={[
                            ['0', 'Just Starting'],
                            ['1-2', '1-2 years'],
                            ['3-5', '3-5 years'],
                            ['5+', '5+ years'],
                          ]} />
                      </div>
                    </StepShell>
                  )}

                  {step === 4 && (
                    <StepShell title="Export Details" subtitle="Your product and market context.">
                      <Textarea label="Products for Export" value={form.products} onChange={(v) => update('products', v)}
                        placeholder="e.g. hibiscus, sesame seeds, cashew" required />
                      <Textarea label="Target Market" value={form.targetMarket} onChange={(v) => update('targetMarket', v)}
                        placeholder="Which countries or regions are you targeting?" required />
                      <Textarea label="Current Challenges" value={form.challenges} onChange={(v) => update('challenges', v)}
                        placeholder="What obstacles are you facing?" required />
                    </StepShell>
                  )}

                  {step === 5 && (
                    <StepShell title="Program Fit" subtitle="Final questions to help us select the right cohort.">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Select label="Availability" value={form.availability} onChange={(v) => update('availability', v)} required
                          options={[
                            ['full', 'Full-time'],
                            ['part', 'Part-time'],
                            ['weekend', 'Weekends Only'],
                          ]} />
                        <Select label="Investment Readiness" value={form.investmentReadiness} onChange={(v) => update('investmentReadiness', v)} required
                          options={[
                            ['ready', 'Ready to invest immediately'],
                            ['within-month', 'Ready within a month'],
                            ['within-quarter', 'Ready within this quarter'],
                            ['exploring', 'Still exploring options'],
                          ]} />
                      </div>
                      <Textarea label="Why should we select you?" value={form.whySelect} onChange={(v) => update('whySelect', v)}
                        placeholder="Share your motivation, goals, and why you'd be a strong fit." rows={5} required />
                    </StepShell>
                  )}

                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setStep((s) => Math.max(1, s - 1))}
                      disabled={step === 1}
                      className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>

                    {step < steps.length ? (
                      <button
                        type="button"
                        onClick={() => canProceed && setStep((s) => s + 1)}
                        disabled={!canProceed}
                        className="inline-flex items-center gap-1 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:bg-blue-300 transition"
                      >
                        Continue <ArrowRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={submitting || !canProceed}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg disabled:bg-emerald-400 transition"
                      >
                        {submitting ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                        ) : (
                          <><Send className="h-4 w-4" /> Submit Application</>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

const StepShell: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({
  title, subtitle, children,
}) => (
  <div className="space-y-4">
    <div>
      <h4 className="text-lg font-bold text-gray-900">{title}</h4>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Input: React.FC<{
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}> = ({ icon: Icon, label, type = 'text', value, onChange, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />}
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm ${Icon ? 'pl-10' : ''}`}
      />
    </div>
  </div>
);

const Textarea: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
}> = ({ label, value, onChange, placeholder, rows = 3, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>
    <textarea
      required={required}
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
    />
  </div>
);

const Select: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
  required?: boolean;
}> = ({ label, value, onChange, options, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>
    <select
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
    >
      <option value="">Select…</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export default EMPApplicationForm;